/**
 * Script Type / Writing System Reference Data
 * 
 * Defines metadata for different writing systems/scripts used across languages.
 * This enables proper text direction, font recommendations, and character set handling.
 */

export interface ScriptType {
  code: string; // Unique identifier (lowercase)
  name: string; // Display name
  nativeName?: string; // Name in the script itself
  direction: 'ltr' | 'rtl'; // Text direction
  unicodeRanges?: string[]; // Unicode ranges for this script
  fontRecommendations?: string[]; // Recommended font families
  description?: string; // Brief description
  exampleLanguages?: string[]; // Languages that use this script
}

/**
 * Comprehensive list of supported scripts
 */
export const SCRIPT_TYPES: ScriptType[] = [
  {
    code: 'latin',
    name: 'Latin',
    nativeName: 'Latin',
    direction: 'ltr',
    unicodeRanges: ['U+0000-U+007F', 'U+0080-U+00FF', 'U+0100-U+017F', 'U+0180-U+024F'],
    fontRecommendations: ['Arial', 'Helvetica', 'Roboto', 'Open Sans'],
    description: 'Standard Latin/Roman alphabet used in Western languages',
    exampleLanguages: ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese']
  },
  {
    code: 'arabic',
    name: 'Arabic',
    nativeName: 'العربية',
    direction: 'rtl',
    unicodeRanges: ['U+0600-U+06FF', 'U+0750-U+077F', 'U+08A0-U+08FF', 'U+FB50-U+FDFF', 'U+FE70-U+FEFF'],
    fontRecommendations: ['Noto Sans Arabic', 'Arial Unicode MS', 'Tahoma', 'Segoe UI'],
    description: 'Arabic script with right-to-left direction and contextual letter forms',
    exampleLanguages: ['Arabic', 'Persian', 'Urdu', 'Pashto']
  },
  {
    code: 'cyrillic',
    name: 'Cyrillic',
    nativeName: 'Кириллица',
    direction: 'ltr',
    unicodeRanges: ['U+0400-U+04FF', 'U+0500-U+052F', 'U+2DE0-U+2DFF', 'U+A640-U+A69F'],
    fontRecommendations: ['Arial', 'Times New Roman', 'Roboto', 'PT Sans'],
    description: 'Cyrillic alphabet used in Slavic languages',
    exampleLanguages: ['Russian', 'Ukrainian', 'Bulgarian', 'Serbian', 'Macedonian', 'Belarusian']
  },
  {
    code: 'hebrew',
    name: 'Hebrew',
    nativeName: 'עברית',
    direction: 'rtl',
    unicodeRanges: ['U+0590-U+05FF', 'U+FB1D-U+FB4F'],
    fontRecommendations: ['Arial', 'David', 'Narkisim', 'Times New Roman'],
    description: 'Hebrew script with right-to-left direction',
    exampleLanguages: ['Hebrew', 'Yiddish']
  },
  {
    code: 'cjk',
    name: 'CJK (Chinese/Japanese/Korean)',
    nativeName: '漢字 / 한자',
    direction: 'ltr',
    unicodeRanges: [
      'U+4E00-U+9FFF', // CJK Unified Ideographs
      'U+3040-U+309F', // Hiragana
      'U+30A0-U+30FF', // Katakana
      'U+AC00-U+D7AF'  // Hangul
    ],
    fontRecommendations: ['Noto Sans CJK', 'Microsoft YaHei', 'SimSun', 'MS Gothic', 'Malgun Gothic'],
    description: 'East Asian ideographic scripts and syllabaries',
    exampleLanguages: ['Chinese (Simplified)', 'Chinese (Traditional)', 'Japanese', 'Korean']
  },
  {
    code: 'devanagari',
    name: 'Devanagari',
    nativeName: 'देवनागरी',
    direction: 'ltr',
    unicodeRanges: ['U+0900-U+097F', 'U+A8E0-U+A8FF'],
    fontRecommendations: ['Noto Sans Devanagari', 'Mangal', 'Kokila'],
    description: 'Devanagari script used for many Indian languages',
    exampleLanguages: ['Hindi', 'Sanskrit', 'Marathi', 'Nepali']
  },
  {
    code: 'thai',
    name: 'Thai',
    nativeName: 'ไทย',
    direction: 'ltr',
    unicodeRanges: ['U+0E00-U+0E7F'],
    fontRecommendations: ['Noto Sans Thai', 'Tahoma', 'Leelawadee', 'Angsana'],
    description: 'Thai script with complex diacritics',
    exampleLanguages: ['Thai']
  },
  {
    code: 'greek',
    name: 'Greek',
    nativeName: 'Ελληνικά',
    direction: 'ltr',
    unicodeRanges: ['U+0370-U+03FF', 'U+1F00-U+1FFF'],
    fontRecommendations: ['Arial', 'Times New Roman', 'Tahoma'],
    description: 'Greek alphabet',
    exampleLanguages: ['Greek']
  },
  {
    code: 'hangul',
    name: 'Hangul',
    nativeName: '한글',
    direction: 'ltr',
    unicodeRanges: ['U+AC00-U+D7AF', 'U+1100-U+11FF', 'U+3130-U+318F'],
    fontRecommendations: ['Noto Sans Korean', 'Malgun Gothic', 'Batang', 'Gulim'],
    description: 'Korean Hangul script',
    exampleLanguages: ['Korean']
  },
  {
    code: 'armenian',
    name: 'Armenian',
    nativeName: 'Հայերեն',
    direction: 'ltr',
    unicodeRanges: ['U+0530-U+058F'],
    fontRecommendations: ['Arial', 'Sylfaen', 'Noto Sans Armenian'],
    description: 'Armenian alphabet',
    exampleLanguages: ['Armenian']
  },
  {
    code: 'georgian',
    name: 'Georgian',
    nativeName: 'ქართული',
    direction: 'ltr',
    unicodeRanges: ['U+10A0-U+10FF'],
    fontRecommendations: ['Sylfaen', 'Noto Sans Georgian'],
    description: 'Georgian script',
    exampleLanguages: ['Georgian']
  }
];

/**
 * Map of script codes to script metadata for quick lookup
 */
export const SCRIPT_TYPE_MAP = new Map<string, ScriptType>(
  SCRIPT_TYPES.map(script => [script.code, script])
);

/**
 * Get all available scripts
 */
export function getAllScripts(): ScriptType[] {
  return SCRIPT_TYPES;
}

/**
 * Get script metadata by code
 */
export function getScriptByCode(code: string): ScriptType | undefined {
  return SCRIPT_TYPE_MAP.get(code.toLowerCase());
}

/**
 * Get the text direction for a script
 */
export function getScriptDirection(code: string): 'ltr' | 'rtl' {
  const script = getScriptByCode(code);
  return script?.direction || 'ltr';
}

/**
 * Check if a script is right-to-left
 */
export function isRTLScript(code: string): boolean {
  return getScriptDirection(code) === 'rtl';
}

/**
 * Get recommended fonts for a script
 */
export function getScriptFonts(code: string): string[] {
  const script = getScriptByCode(code);
  return script?.fontRecommendations || [];
}

/**
 * Get the display name for a script
 */
export function getScriptName(code: string): string {
  const script = getScriptByCode(code);
  return script?.name || code;
}

/**
 * Get the native name for a script (if available)
 */
export function getScriptNativeName(code: string): string | undefined {
  const script = getScriptByCode(code);
  return script?.nativeName;
}

/**
 * Get script options formatted for dropdowns
 */
export function getScriptOptions(): Array<{ value: string; label: string; direction: 'ltr' | 'rtl' }> {
  return SCRIPT_TYPES.map(script => ({
    value: script.code,
    label: script.nativeName ? `${script.name} (${script.nativeName})` : script.name,
    direction: script.direction
  }));
}

/**
 * RTL script codes for quick checking
 */
export const RTL_SCRIPTS = ['arabic', 'hebrew'];

/**
 * Check if given script code is valid
 */
export function isValidScriptCode(code: string): boolean {
  return SCRIPT_TYPE_MAP.has(code.toLowerCase());
}

